import type {
  CargaTrabalhoAlocacao,
  CargaTrabalhoObrasData,
  CargaTrabalhoProfissionaisData,
} from "@/modules/carga-trabalho";
import type { AlocacoesPorLocal, AlocacoesPorObra } from "@/modules/carga-de-trabalho";
import type {
  FornecedorAlocacoes,
  FornecedorConflitoIgnorado,
  ObraAlocacoes,
} from "../recursos.types";
import {
  alteracoesDaAcao,
  chaveAlteracao,
  composicaoEquipeMudou,
  diffEquipe,
  parearEquipe,
  paresComEfeito,
} from "./simulacao.model";
import type {
  AlteracaoEquipe,
  AlteracaoSimulada,
  CenarioSimulacao,
  MembroEquipeSimulado,
  ObraSimulada,
  ParEquipe,
  ProfissionalSimulado,
} from "./simulacao.model";
import { descreverAnterior, descreverSaida, SIMULACAO_MARCA } from "./simulacao.textos";

/**
 * Aplica o cenário sobre os dados de cada perspectiva, já no formato que elas consomem,
 * para que cada uma reaproveite a própria regra de conflito e o próprio Gantt. As marcas
 * apontam, pela chave que o Gantt já usa (id da alocação ou da obra), o que foi alterado,
 * com o tooltip do valor anterior. Conflito ignorado entra como um `conflitoIgnorado` real.
 */
export interface MarcaSimulacao {
  /** Tooltip em texto: o selo e o valor anterior, uma informação por linha. */
  texto: string;
  /** Alteração que originou a marca; a saída de uma obra (linha de quem saiu) não a tem. */
  alteracao?: AlteracaoSimulada;
}

export type MarcasSimulacao = ReadonlyMap<string, MarcaSimulacao>;

export const SEM_MARCAS: MarcasSimulacao = new Map();

export interface CenarioAplicado<T> {
  dados: T;
  marcas: MarcasSimulacao;
}

/** Tooltip da marca: o selo e o valor anterior, uma informação por linha. */
export function tooltipMarca(alteracao: AlteracaoSimulada): MarcaSimulacao {
  return { texto: `${SIMULACAO_MARCA}\n${descreverAnterior(alteracao)}`, alteracao };
}

/**
 * Chave da marca de quem sai (profissional, fornecedor ou Local): a alocação foi para
 * outro recurso, então a linha dele não tem mais a barra marcada — a marca vai na linha.
 */
export function marcaSaidaKey(recursoId: string): string {
  return `saida:${recursoId}`;
}

/** Marca a linha de quem saiu da obra; saídas de várias obras somam linhas no tooltip. */
function marcarSaida(
  marcas: Map<string, MarcaSimulacao>,
  recursoId: string,
  obra: Pick<ObraSimulada, "codigo" | "embarcacao">,
): void {
  const key = marcaSaidaKey(recursoId);
  marcas.set(key, {
    texto: `${marcas.get(key)?.texto ?? SIMULACAO_MARCA}\n${descreverSaida(obra)}`,
  });
}

/** Id da alocação que a simulação cria para um membro da equipe nova. */
export function alocacaoSimuladaId(
  alteracao: AlteracaoEquipe,
  membro: MembroEquipeSimulado,
  index: number,
): string {
  return `${chaveAlteracao(alteracao)}:${membro.usuarioId}:${index}`;
}

/** Id da alocação do par: a gravada no novo, no anterior ou, sem nenhuma, a que a simulação cria. */
function alocacaoDoPar(alteracao: AlteracaoEquipe, { membro, previa, index }: ParEquipe): string {
  return membro.alocacaoId ?? previa?.alocacaoId ?? alocacaoSimuladaId(alteracao, membro, index);
}

/** O que identifica a alocação do par: o profissional e a alocação gravada, no novo ou no anterior. */
function alvoDoPar({ membro, previa }: ParEquipe) {
  return { usuarioId: membro.usuarioId, alocacaoId: membro.alocacaoId ?? previa?.alocacaoId };
}

/**
 * A alocação `existente` é a do par: pela alocação gravada quando as duas a têm e, sem ela
 * de um dos lados, pelo profissional.
 */
function mesmaAlocacao(
  existente: { alocacaoId?: string; usuarioId: string },
  alvo: { alocacaoId?: string; usuarioId: string },
): boolean {
  return existente.alocacaoId && alvo.alocacaoId
    ? existente.alocacaoId === alvo.alocacaoId
    : existente.usuarioId === alvo.usuarioId;
}

/**
 * Remanejar uma alocação que só existe no cenário (de uma equipe simulada) não vira um
 * PATCH: troca o profissional na própria equipe simulada. `null` se a alocação é real.
 */
export function remanejarNaEquipeSimulada(
  cenario: CenarioSimulacao,
  alocacaoId: string,
  substituto: ProfissionalSimulado,
  justificativa?: string,
): AlteracaoEquipe | null {
  for (const alteracao of alteracoesDaAcao(cenario, "definir-equipe")) {
    const index = alteracao.novo.findIndex(
      (membro, i) => alocacaoSimuladaId(alteracao, membro, i) === alocacaoId,
    );
    if (index === -1) continue;
    return {
      ...alteracao,
      ...(justificativa ? { justificativa } : {}),
      novo: alteracao.novo.map((membro, i) =>
        i === index
          ? { ...membro, usuarioId: substituto.id, nome: substituto.nome, time: substituto.time }
          : membro,
      ),
    };
  }
  return null;
}

/** Põe a alocação na linha `destinoId`, criando a linha quando ela não está nos dados. */
type LinhaComAlocacoes = { id: string; alocacoes: { id: string }[] };
type AlocacaoDa<R extends LinhaComAlocacoes> = R["alocacoes"][number];

function adicionarAlocacao<R extends LinhaComAlocacoes>(
  linhas: R[],
  destinoId: string,
  alocacao: AlocacaoDa<R>,
  novaLinha: (alocacao: AlocacaoDa<R>) => R,
): R[] {
  return linhas.some((linha) => linha.id === destinoId)
    ? linhas.map((linha) =>
        linha.id === destinoId ? { ...linha, alocacoes: [...linha.alocacoes, alocacao] } : linha,
      )
    : [...linhas, novaLinha(alocacao)];
}

/** Move a alocação para a linha `destinoId`; `null` se ela não está nos dados consultados. */
function moverAlocacao<R extends LinhaComAlocacoes>(
  linhas: R[],
  alocacaoId: string,
  destinoId: string,
  novaLinha: (alocacao: AlocacaoDa<R>) => R,
): R[] | null {
  const alocacao = linhas
    .flatMap((linha) => linha.alocacoes)
    .find((item) => item.id === alocacaoId);
  if (!alocacao) return null;

  const sem = linhas.map((linha) => ({
    ...linha,
    alocacoes: linha.alocacoes.filter((item) => item.id !== alocacaoId),
  }));
  return adicionarAlocacao(sem, destinoId, alocacao, novaLinha);
}

/* Profissionais (US-1779): agrupado por profissional e por obra. */

export function aplicarCenarioProfissionais(
  data: CargaTrabalhoProfissionaisData,
  cenario: CenarioSimulacao,
): CenarioAplicado<CargaTrabalhoProfissionaisData> {
  const marcas = new Map<string, MarcaSimulacao>();
  let profissionais = data.profissionais;

  for (const alteracao of cenario.alteracoes) {
    const marca = tooltipMarca(alteracao);

    if (alteracao.acao === "definir-equipe") {
      const { removed } = parearEquipe(alteracao.anterior, alteracao.novo);
      const efeito = paresComEfeito(alteracao.anterior, alteracao.novo);

      removed.forEach((membro) => marcarSaida(marcas, membro.usuarioId, alteracao.obra));
      if (removed.length > 0) {
        profissionais = profissionais.map((profissional) => ({
          ...profissional,
          alocacoes: profissional.alocacoes.filter(
            (alocacao) =>
              alocacao.obra.id !== alteracao.obra.id ||
              !removed.some((membro) =>
                mesmaAlocacao({ usuarioId: profissional.id, alocacaoId: alocacao.id }, membro),
              ),
          ),
        }));
      }

      for (const par of efeito.filter(({ previa }) => previa)) {
        const { membro } = par;
        const alvo = alvoDoPar(par);
        // O mesmo profissional em duas linhas da obra: só a alocação do par muda.
        const dono = profissionais.find((profissional) => profissional.id === membro.usuarioId);
        const doPar = dono?.alocacoes.find(
          (alocacao) =>
            alocacao.obra.id === alteracao.obra.id &&
            mesmaAlocacao({ usuarioId: membro.usuarioId, alocacaoId: alocacao.id }, alvo),
        );
        if (dono && doPar) {
          marcas.set(doPar.id, marca);
          profissionais = profissionais.map((profissional) =>
            profissional === dono
              ? {
                  ...profissional,
                  alocacoes: profissional.alocacoes.map((alocacao) =>
                    alocacao === doPar
                      ? {
                          ...alocacao,
                          papelObraId: membro.papelObraId,
                          inicio: membro.inicio,
                          fim: membro.fim,
                        }
                      : alocacao,
                  ),
                }
              : profissional,
          );
          continue;
        }
        // Já tem alocação na obra, mas nenhuma é a do par: não duplica.
        if (dono?.alocacoes.some((alocacao) => alocacao.obra.id === alteracao.obra.id)) continue;

        const alocacao: CargaTrabalhoAlocacao = {
          id: alocacaoDoPar(alteracao, par),
          papelObraId: membro.papelObraId,
          inicio: membro.inicio,
          fim: membro.fim,
          obra: alteracao.obra,
        };
        marcas.set(alocacao.id, marca);
        profissionais = adicionarAlocacao(profissionais, membro.usuarioId, alocacao, (item) => ({
          id: membro.usuarioId,
          nome: membro.nome,
          time: membro.time,
          alocacoes: [item],
          indisponibilidades: [],
        }));
      }

      efeito
        .filter(({ previa }) => !previa)
        .forEach(({ membro, index }) => {
          const alocacao: CargaTrabalhoAlocacao = {
            id: alocacaoSimuladaId(alteracao, membro, index),
            papelObraId: membro.papelObraId,
            inicio: membro.inicio,
            fim: membro.fim,
            obra: alteracao.obra,
          };
          marcas.set(alocacao.id, marca);
          profissionais = adicionarAlocacao(profissionais, membro.usuarioId, alocacao, (item) => ({
            id: membro.usuarioId,
            nome: membro.nome,
            time: membro.time,
            alocacoes: [item],
            indisponibilidades: [],
          }));
        });
    }

    if (alteracao.acao === "remanejar-profissional") {
      const movidos = moverAlocacao(
        profissionais,
        alteracao.alocacaoId,
        alteracao.novo.id,
        (item) => ({
          ...alteracao.novo,
          alocacoes: [item],
          indisponibilidades: [],
        }),
      );
      if (movidos) {
        profissionais = movidos;
        marcas.set(alteracao.alocacaoId, marca);
        marcarSaida(marcas, alteracao.anterior.id, alteracao.obra);
      }
    }

    if (alteracao.acao === "ignorar-conflito-profissional") {
      profissionais = profissionais.map((profissional) =>
        profissional.id === alteracao.profissional.id
          ? {
              ...profissional,
              conflitosIgnorados: [
                ...(profissional.conflitosIgnorados ?? []),
                { alocacaoIds: alteracao.alocacaoIds },
              ],
            }
          : profissional,
      );
      alteracao.alocacaoIds.forEach((id) => marcas.set(id, marca));
    }
  }

  return { dados: { ...data, profissionais }, marcas };
}

/** Chave da linha de um profissional na visão por Obras (a mesma de `buildObraGroups`). */
export function membroObraKey(obraId: string, usuarioId: string): string {
  return `${obraId}-${usuarioId}`;
}

/**
 * Chave da marca da linha de um profissional na visão por Obras: a alocação quando há, para
 * que duas linhas do mesmo profissional na obra não dividam a marca.
 */
export function chaveMarcaMembro(
  obraId: string,
  membro: { id: string; alocacaoId?: string },
): string {
  return membro.alocacaoId ?? membroObraKey(obraId, membro.id);
}

export function aplicarCenarioObras(
  data: CargaTrabalhoObrasData,
  cenario: CenarioSimulacao,
): CenarioAplicado<CargaTrabalhoObrasData> {
  const marcas = new Map<string, MarcaSimulacao>();
  let obras = data.obras;

  for (const alteracao of cenario.alteracoes) {
    const marca = tooltipMarca(alteracao);

    if (alteracao.acao === "definir-equipe") {
      obras = obras.map((obra) => {
        if (obra.id !== alteracao.obra.id) return obra;
        const diff = diffEquipe(alteracao.anterior, alteracao.novo);
        if (composicaoEquipeMudou(diff)) marcas.set(obra.id, marca);

        const { removed } = parearEquipe(alteracao.anterior, alteracao.novo);
        const efeito = paresComEfeito(alteracao.anterior, alteracao.novo);
        const alterados = efeito.filter(({ previa }) => previa);
        const casados = new Set<ParEquipe>();
        removed.forEach((membro) => marcarSaida(marcas, membro.usuarioId, alteracao.obra));

        const restantes = obra.profissionais
          .filter(
            (membro) =>
              !removed.some((saiu) =>
                mesmaAlocacao({ usuarioId: membro.id, alocacaoId: membro.alocacaoId }, saiu),
              ),
          )
          .map((membro) => {
            // O mesmo profissional em duas linhas: só a linha alterada muda.
            const par = alterados.find(
              (item) =>
                !casados.has(item) &&
                mesmaAlocacao(
                  { usuarioId: membro.id, alocacaoId: membro.alocacaoId },
                  alvoDoPar(item),
                ),
            );
            if (!par) return membro;
            casados.add(par);
            marcas.set(chaveMarcaMembro(obra.id, membro), marca);
            return {
              ...membro,
              papelObraId: par.membro.papelObraId,
              inicio: par.membro.inicio,
              fim: par.membro.fim,
            };
          });

        const extra = [
          ...alterados.filter((par) => !casados.has(par)),
          ...efeito.filter(({ previa }) => !previa),
        ].map((par) => {
          const { membro } = par;
          const alocacaoId = alocacaoDoPar(alteracao, par);
          marcas.set(chaveMarcaMembro(obra.id, { id: membro.usuarioId, alocacaoId }), marca);
          return {
            id: membro.usuarioId,
            alocacaoId,
            nome: membro.nome,
            time: membro.time,
            papelObraId: membro.papelObraId,
            inicio: membro.inicio,
            fim: membro.fim,
          };
        });

        return { ...obra, profissionais: [...restantes, ...extra] };
      });
    }

    if (alteracao.acao === "remanejar-profissional") {
      obras = obras.map((obra) => {
        if (obra.id !== alteracao.obra.id) return obra;
        return {
          ...obra,
          profissionais: obra.profissionais.map((membro) => {
            if (membro.id !== alteracao.anterior.id) return membro;
            marcas.set(obra.id, marca);
            marcas.set(
              chaveMarcaMembro(obra.id, { id: alteracao.novo.id, alocacaoId: membro.alocacaoId }),
              marca,
            );
            return { ...membro, ...alteracao.novo };
          }),
        };
      });
    }

    if (alteracao.acao === "ignorar-conflito-profissional") {
      // O conflito tratado sai do selo das obras envolvidas, que ficam marcadas.
      const envolvidas = new Set(alteracao.obras.map((obra) => obra.id));
      obras = obras.map((obra) =>
        envolvidas.has(obra.id)
          ? {
              ...obra,
              conflitosIgnorados: [
                ...(obra.conflitosIgnorados ?? []),
                { alocacaoIds: alteracao.alocacaoIds },
              ],
            }
          : obra,
      );
      alteracao.obras.forEach((obra) =>
        marcas.set(membroObraKey(obra.id, alteracao.profissional.id), marca),
      );
      obras
        .filter((obra) => envolvidas.has(obra.id))
        .flatMap((obra) => obra.profissionais.map((membro) => ({ obra, membro })))
        .filter(
          ({ membro }) =>
            membro.id === alteracao.profissional.id &&
            membro.alocacaoId &&
            alteracao.alocacaoIds.includes(membro.alocacaoId),
        )
        .forEach(({ obra, membro }) => marcas.set(chaveMarcaMembro(obra.id, membro), marca));
    }
  }

  return { dados: { ...data, obras }, marcas };
}

/* Fornecedor (US-1774 / HU 2041): agrupado por fornecedor e por obra. */

function registroIgnoradoFornecedor(
  alteracao: Extract<AlteracaoSimulada, { acao: "ignorar-conflito-fornecedor" }>,
): FornecedorConflitoIgnorado {
  return {
    id: chaveAlteracao(alteracao),
    fornecedorId: alteracao.fornecedor.id,
    conflitoInicio: alteracao.conflito.inicio,
    conflitoFim: alteracao.conflito.fim,
    alocacaoIds: alteracao.alocacaoIds,
  };
}

export function aplicarCenarioFornecedores(
  fornecedores: FornecedorAlocacoes[],
  cenario: CenarioSimulacao,
): CenarioAplicado<FornecedorAlocacoes[]> {
  const marcas = new Map<string, MarcaSimulacao>();
  let dados = fornecedores;

  for (const alteracao of cenario.alteracoes) {
    if (alteracao.acao === "remanejar-fornecedor") {
      // Fora do período ou da busca consultados: não há o que mover.
      const movidos = moverAlocacao(dados, alteracao.alocacaoId, alteracao.novo.id, (item) => ({
        ...alteracao.novo,
        alocacoes: [item],
      }));
      if (movidos) {
        dados = movidos;
        marcas.set(alteracao.alocacaoId, tooltipMarca(alteracao));
        marcarSaida(marcas, alteracao.anterior.id, alteracao.obra);
      }
    }

    if (alteracao.acao === "ignorar-conflito-fornecedor") {
      const registro = registroIgnoradoFornecedor(alteracao);
      dados = dados.map((fornecedor) =>
        (fornecedor.id || fornecedor.nome) === alteracao.fornecedor.id
          ? {
              ...fornecedor,
              conflitosIgnorados: [...(fornecedor.conflitosIgnorados ?? []), registro],
            }
          : fornecedor,
      );
      alteracao.alocacaoIds.forEach((id) => marcas.set(id, tooltipMarca(alteracao)));
    }
  }

  return { dados, marcas };
}

export function aplicarCenarioObrasFornecedor(
  obras: ObraAlocacoes[],
  cenario: CenarioSimulacao,
): CenarioAplicado<ObraAlocacoes[]> {
  const marcas = new Map<string, MarcaSimulacao>();
  let dados = obras;

  for (const alteracao of cenario.alteracoes) {
    if (alteracao.acao === "remanejar-fornecedor") {
      dados = dados.map((obra) => ({
        ...obra,
        alocacoes: obra.alocacoes.map((alocacao) => {
          if (alocacao.id !== alteracao.alocacaoId) return alocacao;
          marcas.set(alocacao.id, tooltipMarca(alteracao));
          return { ...alocacao, fornecedor: alteracao.novo };
        }),
      }));
    }

    if (alteracao.acao === "ignorar-conflito-fornecedor") {
      const ids = new Set(alteracao.alocacaoIds);
      const registro = registroIgnoradoFornecedor(alteracao);
      dados = dados.map((obra) =>
        obra.alocacoes.some((alocacao) => ids.has(alocacao.id))
          ? { ...obra, conflitosIgnorados: [...(obra.conflitosIgnorados ?? []), registro] }
          : obra,
      );
      ids.forEach((id) => marcas.set(id, tooltipMarca(alteracao)));
    }
  }

  return { dados, marcas };
}

/* Local (US-2023 / HU 2042): agrupado por Local e por obra. */

export function aplicarCenarioLocais(
  data: AlocacoesPorLocal,
  cenario: CenarioSimulacao,
): CenarioAplicado<AlocacoesPorLocal> {
  const marcas = new Map<string, MarcaSimulacao>();
  let { locais, conflitosIgnorados } = data;

  for (const alteracao of cenario.alteracoes) {
    if (alteracao.acao === "remanejar-local") {
      const movidos = moverAlocacao(locais, alteracao.alocacaoId, alteracao.novo.id, (item) => ({
        ...alteracao.novo,
        alocacoes: [item],
      }));
      if (movidos) {
        locais = movidos;
        marcas.set(alteracao.alocacaoId, tooltipMarca(alteracao));
        marcarSaida(marcas, alteracao.anterior.id, alteracao.obra);
      }
    }

    if (alteracao.acao === "ignorar-conflito-local") {
      conflitosIgnorados = [
        ...conflitosIgnorados,
        {
          id: chaveAlteracao(alteracao),
          localId: alteracao.local.id,
          conflitoInicio: alteracao.conflito.inicio,
          conflitoFim: alteracao.conflito.fim,
          alocacaoIds: alteracao.alocacaoIds,
        },
      ];
      alteracao.alocacaoIds.forEach((id) => marcas.set(id, tooltipMarca(alteracao)));
    }
  }

  return { dados: { ...data, locais, conflitosIgnorados }, marcas };
}

export function aplicarCenarioObrasLocal(
  data: AlocacoesPorObra,
  cenario: CenarioSimulacao,
): CenarioAplicado<AlocacoesPorObra> {
  const marcas = new Map<string, MarcaSimulacao>();
  let obras = data.obras;

  for (const alteracao of cenario.alteracoes) {
    if (alteracao.acao === "remanejar-local") {
      obras = obras.map((obra) => ({
        ...obra,
        alocacoes: obra.alocacoes.map((alocacao) => {
          if (alocacao.id !== alteracao.alocacaoId) return alocacao;
          marcas.set(alocacao.id, tooltipMarca(alteracao));
          return { ...alocacao, local: alteracao.novo };
        }),
      }));
    }

    if (alteracao.acao === "ignorar-conflito-local") {
      // A visão por Obra não distingue conflitos tratados: só a marca nas alocações.
      alteracao.alocacaoIds.forEach((id) => marcas.set(id, tooltipMarca(alteracao)));
    }
  }

  return { dados: { ...data, obras }, marcas };
}
